<template>
  <template v-if="withPosition">
    <component
      :is="instance.getFormPosition()"
      label="Position"
      :instance
      :disabled="loading"
      :focus-on-load="focusPositionOnLoad"
    />
  </template>
  <component
    :is="instance.getFormDate()"
    :label="CASHFLOW_PROJECTION_FIELD_LABEL[CASHFLOW_PROJECTION_FIELD.DATE]"
    :instance
    :disabled="loading"
    :focus-on-load="focusDateOnLoad"
  />
  <component
    :is="instance.getFormValue()"
    :label="CASHFLOW_PROJECTION_FIELD_LABEL[CASHFLOW_PROJECTION_FIELD.VALUE]"
    :instance
    :disabled="loading"
    :focus-on-load="focusValueOnLoad"
  />
  <component
    :is="instance.getFormDescription()"
    :label="CASHFLOW_PROJECTION_FIELD_LABEL[CASHFLOW_PROJECTION_FIELD.DESCRIPTION]"
    :instance
    :disabled="loading"
  />
  <UIButtonsGroup>
    <template #default="{ short }">
      <UIButtonClose :short @click="handleClose" />
      <UIButtonSave
        ref="buttonSaveRef"
        :short
        :disabled="isButtonDisabled"
        :loading
        @click="handleSave"
      />
    </template>
  </UIButtonsGroup>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue'

import { CashFlowProjectionClass } from '@types'

import {
  CASHFLOW_PROJECTION_FIELD,
  CASHFLOW_PROJECTION_FIELD_LABEL,
  NOTIFICATION_DELAY,
  CASHFLOW_PROJECTION_ID_PREFIX,
} from '@/const'

import { useNotifications } from '@/plugins/notification'
import { handleCatchedError } from '@/helpers/common'

import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'

type Props = {
  instance: CashFlowProjectionClass
  withPosition?: boolean
}

type Emits = {
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { progress, update } = useNotifications()

const loading = ref(false)

const isButtonDisabled = computed(() =>
  loading.value ||
  !props.instance.field(CASHFLOW_PROJECTION_FIELD.DATE).value ||
  !props.instance.field(CASHFLOW_PROJECTION_FIELD.VALUE).value
)

const isSavedCashFlowProjection = computed(() => props.instance.id.startsWith(CASHFLOW_PROJECTION_ID_PREFIX))
provide('isSavedCashFlowProjection', isSavedCashFlowProjection)

const focusPositionOnLoad = computed(
  () => !isSavedCashFlowProjection.value &&
    (!props.instance.field(CASHFLOW_PROJECTION_FIELD.ACCOUNT_ID).value ||
    !props.instance.field(CASHFLOW_PROJECTION_FIELD.ASSET_ID).value)
)

const focusDateOnLoad = computed(
  () =>
    !isSavedCashFlowProjection.value &&
    !focusPositionOnLoad.value &&
    !props.instance.field(CASHFLOW_PROJECTION_FIELD.DATE).value,
)

const focusValueOnLoad = computed(
  () =>
    !isSavedCashFlowProjection.value &&
    !focusPositionOnLoad.value &&
    !focusDateOnLoad.value &&
    !props.instance.field(CASHFLOW_PROJECTION_FIELD.VALUE).value,
)

const handleClose = () => {
  emit('close')
}

const handleSave = async () => {
  loading.value = true
  const isNew = props.instance.isNew
  const nid = await progress({
    message: `${isNew ? 'Creating' : 'Updating'} cash flow projection`,
  })
  try {
    if (isNew) {
      await props.instance.store()
    } else {
      await props.instance.update()
    }
    await update(
      nid,
      {
        type: 'success',
        message: `Cash flow projection ${props.instance.field(CASHFLOW_PROJECTION_FIELD.DATE).value} ${
          isNew ? 'created' : 'updated'
        }`,
      },
      NOTIFICATION_DELAY,
    )
    emit('close')
  } catch (e) {
    if (e instanceof Error) {
      update(nid, {
        type: 'error',
        message: e.message as string,
      })
    }
    handleCatchedError(e as string, props.instance.get())
  } finally {
    loading.value = false
  }
}
</script>
